// The Briefing strip above the SmartBox while a search is being turned into a
// Research Pack (busy-my-web#15, frame 40006448:205688).
//
// The frame shows it as a heading naming the context, then what has been picked:
//
//   Web search
//   Selected
//     3 photos
//     2 snippets
//
// Every briefing in the design has that shape — a line saying WHICH thing is
// being briefed, then a short count list. The pack view's is "Prada Sunglasses
// briefing / 2 snippets / 1 task"; the Email Workspace's is a sentence plus its
// suggestions. So the counts are the variable part and the naming line is the
// constant, which is why they are split here rather than formatted as one string.

import { typeTitle } from '@/lib/globalTimelineFeed'
import { countLabel } from '@/lib/briefings/rows'

/**
 * "Web search" — the line that names the context.
 *
 * The QUERY used to be that line ("Briefing web researching - Buy sunglasses"), and this is the
 * strip that is on screen at the moment of SAVING, which is exactly where Saffet hit it
 * (busy-my-web#218, 2026-08-26): a real search — "looking for a chocolate cake with three layers
 * and flowers on top with a silver base and candles" — turned the heading into a paragraph the row
 * could not hold. The kind word is the same one lib/briefings/search.js now leads with and the same
 * one the feed and the story rail say, so the two research briefings read as one vocabulary.
 */
export function briefingTitle() {
  return typeTitle('research')
}

/**
 * What has been picked, counted by kind and worded as the frame words it.
 *
 * A snippet is any piece of the ANSWER — the design calls the ticked answer
 * lines "snippets", and they arrive as `answerChunks`/notes rather than under
 * one kind, so anything that is not a photo or a link counts as one.
 *
 * Kinds with nothing picked are dropped: "0 links" is noise on a strip whose
 * whole job is to say what you have.
 *
 * @returns {Array<{key: string, label: string, count: number}>}
 */
export function briefingCounts(items) {
  const list = (items || []).filter(Boolean)
  const of = (kind) => list.filter(i => i.kind === kind).length
  const photos = of('photo')
  // A ticked shop result (Best prices) counts as a link — it is one, a page the pack will cite
  // (Pouria 2026-10-21: "it said 1 sources we didn't have it any more it should be count as the
  // link as well"). This retires #599's separate "Source" row; the classifier still calls the
  // kind `product` (lib/briefings/search.js), only the strip folds it into links.
  const links = of('link') + of('product')
  // A ticked video or product is not an Extraction (Pouria, 2026-10-01, a YouTube result ticked
  // and the strip said "1 Extraction": "this one is not extraction we need to add link and videos
  // as well") — the same rule the pack's own counts follow since busy-my-web#546.
  const videos = of('video')
  const snippets = list.length - photos - links - videos

  // busy-my-web#609 (Saffet 2026-10-09): "2 Photos", "1,204 Links" — the shared `countLabel`.
  const plural = (n, word) => countLabel(n, word)

  return [
    { key: 'photo', count: photos, label: plural(photos, 'photo') },
    { key: 'snippet', count: snippets, label: plural(snippets, 'Extraction') },
    { key: 'video', count: videos, label: plural(videos, 'video') },
    { key: 'link', count: links, label: plural(links, 'link') },
  ].filter(row => row.count > 0)
}
